// Phase NAATI-MAX B2 — /en/translate/naati/ccl-test-prep
import { createFileRoute } from "@tanstack/react-router";
import NaatiKnowledgePage from "@/components/NaatiKnowledgePage";
import { CCL_FACTS_EN, CCL_PREP_PACK_TH } from "@/data/naati-ccl";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const URL = `${SITE}/en/translate/naati/ccl-test-prep`;

export const Route = createFileRoute("/en/translate/naati/ccl-test-prep")({
  head: () => ({
    meta: [
      { title: ctaTitle("NAATI CCL Test Prep Thai-English | +5 Migration Points", "/en/translate/naati/ccl-test-prep", "en")},
      { name: "description", content: ctaDesc("Thai-English NAATI CCL Test prep — 50 hours, 30 mock dialogues (Health/Legal/Centrelink/Education). Get your timeline and checklist in minutes — chat now.", "/en/translate/naati/ccl-test-prep", "en")},
      { property: "og:title", content: ctaTitle("NAATI CCL Test Prep Thai-English | +5 Migration Points", "/en/translate/naati/ccl-test-prep", "en")},
      { property: "og:description", content: ctaDesc("Thai-English NAATI CCL Test prep — 50 hours, 30 mock dialogues (Health/Legal/Centrelink/Education). Claim 5 bonus points for 189/190/491.", "/en/translate/naati/ccl-test-prep", "en")},
      { property: "og:url", content: URL },
    ],
    links: canonicalAndHreflang("/translate/naati/ccl-test-prep", "/en/translate/naati/ccl-test-prep"),
    scripts: [
      { type: "application/ld+json", children: JSON.stringify({
        "@context": "https://schema.org", "@type": "Course",
        name: "NAATI CCL Test Prep (Thai-English)",
        description: "50-hour CCL preparation course for the 5-point Skilled Migration bonus.",
        provider: { "@type": "Organization", name: "NYC Language Institute", sameAs: SITE },
        inLanguage: "en", url: URL,
        offers: { "@type": "Offer", price: CCL_PREP_PACK_TH.priceThb, priceCurrency: "THB", availability: "https://schema.org/InStock" },
        hasCourseInstance: { "@type": "CourseInstance", courseMode: "Online", courseWorkload: `PT${CCL_PREP_PACK_TH.hoursRecommended}H` },
      }) },
      { type: "application/ld+json", children: JSON.stringify({
        "@context": "https://schema.org", "@type": "FAQPage", inLanguage: "en",
        mainEntity: CCL_FACTS_EN.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      }) },
      { type: "application/ld+json", children: JSON.stringify({
        "@context": "https://schema.org", "@type": "WebPage", "@id": `${URL}#speakable`, url: URL, name: "NAATI Certified Translation",
        speakable: { "@type": "SpeakableSpecification", cssSelector: ["[data-speakable]", "h1", ".direct-answer"] },
      }) },
    ],
  }),
  component: () => (
    <NaatiKnowledgePage
      locale="en"
      eyebrow="CCL Test Prep · Bonus 5 Points"
      h1="NAATI CCL Test Prep, Thai-English — Earn 5 Bonus Skilled Migration Points"
      lead="Preparation course for the NAATI Credentialed Community Language (CCL) Test for subclass 189 / 190 / 491 applicants. 50 hours, 30 mock dialogues across all NAATI source domains."
      speakableAnswer={`The NAATI CCL Test consists of two 300-word dialogues (Thai↔English). A pass adds 5 bonus points to the SkillSelect EOI. Our 50-hour prep pack costs THB ${CCL_PREP_PACK_TH.priceThb.toLocaleString()}.`}
      sections={[
        { heading: "What's in the pack", bullets: CCL_PREP_PACK_TH.inclusionsEn },
        {
          heading: "Domains covered",
          paragraph: "NAATI draws live dialogues from 7 source domains — we drill them all.",
          bullets: CCL_PREP_PACK_TH.domainsCovered.map((d) => `${d} — NAATI-style mock dialogues`),
        },
        {
          heading: "Who is this for?",
          bullets: [
            "EOI lodgers for 189 / 190 / 491 chasing the 5-point bonus",
            "Candidates already at IELTS 6.5+ or PTE 65+",
            "Anyone sitting in the next 6-12 months",
            "Re-sitters after a previous fail",
          ],
        },
        {
          heading: "CCL vs NAATI Translation",
          paragraph: "The CCL Test is not the same as a NAATI translation. CCL is a points-earning test. NAATI translation is a stamped document service used for visa lodgement. A passed CCL candidate still needs NAATI translations of transcripts, police clearance, and marriage certificates to lodge.",
        },
      ]}
      faqs={CCL_FACTS_EN}
      cta={{ label: "Enrol in the CCL prep course", href: "/en/translate/naati" }}
    />
  ),
});
